import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from "@mui/material";
import {
  SkipNext,
  CheckCircle,
  CloudUpload,
  Settings,
  Edit,
  Refresh,
} from "@mui/icons-material";
import {
  startPreprocess,
  confirmBatchConfiguration,
  generateBulletPointDescription,
} from "../../../../api/batches";
import { BatchConfigurationProps, ColumnMapping } from "./types";
import { BulletPointType } from "../../../../pages/playground/types";
import { MasterFileUploadSection } from "./master-file-upload-section";
import { CustomBulletPointsSection } from "./custom-bullet-points-section";

export const BatchConfiguration: React.FC<BatchConfigurationProps> = ({
  batchId,
  hasGdriveFolder,
  existingMappings = [],
  sheetData,
  canSeeBatchConfiguration,
  isFetchingSheetData = false,
  isReconfiguring = false,
  onComplete,
  onSheetUploaded,
  onCancel,
}) => {
  // Stepper state - always start at step 0 (upload) unless reconfiguring with existing data
  // User must manually click "Continue to Settings" to advance
  // If reconfiguring but no sheet data, treat as fresh start
  const [activeStep, setActiveStep] = useState(isReconfiguring && sheetData ? 1 : 0);

  // State
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [customBpExpanded, setCustomBpExpanded] = useState(true);
  const [autoCompleting, setAutoCompleting] = useState(false);
  const [mappingsInitialized, setMappingsInitialized] = useState(false);

  // Helper to navigate between steps
  const goToStep = (step: number) => {
    setActiveStep(step);
  };

  // Auto-complete configuration for users who cannot see batch configuration
  const autoCompleteConfiguration = useCallback(async () => {
    try {
      // Confirm configuration
      await confirmBatchConfiguration({ batchId });

      // Start preprocessing
      await startPreprocess({ batchId });

      onComplete();
    } catch (err) {
      setError("Failed to complete configuration. Please try again.");
      setAutoCompleting(false);
    }
  }, [batchId, onComplete]);

  // Auto-complete for users who cannot see batch configuration
  useEffect(() => {
    if (!canSeeBatchConfiguration && !autoCompleting && sheetData) {
      setAutoCompleting(true);
      autoCompleteConfiguration();
    }
  }, [
    canSeeBatchConfiguration,
    autoCompleting,
    autoCompleteConfiguration,
    sheetData,
  ]);

  // Initialize column mappings from sheetData prop
  useEffect(() => {
    if (!sheetData || mappingsInitialized) return;

    // Get all columns including the first one
    const columns = sheetData.columns || [];

    // Build mappings with sample values
    const mappings: ColumnMapping[] = columns.map((column) => {
      // Get sample values from first 5 items (full values, no truncation)
      const sampleValues = (sheetData.items || [])
        .slice(0, 5)
        .map((item) => item[column])
        .filter((val) => val && val.toString().trim())
        .map((val) => val.toString().trim());

      // Check if this column was previously mapped
      const existingMapping = existingMappings.find(
        (m) => m.column_name === column
      );

      // When reconfiguring, only select columns that were previously mapped
      // For new setup, select all columns by default
      const isSelected = isReconfiguring
        ? !!existingMapping
        : true;

      // Infer mapped_to_type: if custom_name exists but mapped_to_type is undefined,
      // it means the column was saved as CUSTOM (since CUSTOM is saved as undefined in the backend)
      const inferredMappedToType = existingMapping?.mapped_to_type
        ? existingMapping.mapped_to_type
        : existingMapping?.custom_name
          ? "CUSTOM"
          : undefined;

      return {
        column_name: column,
        custom_name: existingMapping?.custom_name || "",
        description: existingMapping?.description || "",
        selected: isSelected,
        sample_values: sampleValues,
        mapped_to_type: inferredMappedToType,
        isGeneratingDescription: false,
      };
    });

    setColumnMappings(mappings);
    setMappingsInitialized(true);
  }, [sheetData, existingMappings, mappingsInitialized, isReconfiguring]);

  // Handlers
  const handleToggleColumn = (index: number) => {
    setColumnMappings((prev) =>
      prev.map((mapping, i) => {
        if (i !== index) return mapping;

        // If deselecting, reset all mapping values
        if (mapping.selected) {
          return {
            ...mapping,
            selected: false,
            mapped_to_type: undefined,
            custom_name: "",
            description: "",
          };
        }

        // If selecting, just toggle selected
        return { ...mapping, selected: true };
      })
    );
  };

  const handleCustomNameChange = (index: number, value: string) => {
    setColumnMappings((prev) =>
      prev.map((mapping, i) =>
        i === index ? { ...mapping, custom_name: value } : mapping
      )
    );
  };

  const handleMappingDescriptionChange = (index: number, value: string) => {
    setColumnMappings((prev) =>
      prev.map((mapping, i) =>
        i === index ? { ...mapping, description: value } : mapping
      )
    );
  };

  const handleMappedTypeChange = (
    index: number,
    type: BulletPointType | "CUSTOM" | undefined
  ) => {
    setColumnMappings((prev) =>
      prev.map((mapping, i) =>
        i === index
          ? {
              ...mapping,
              mapped_to_type: type,
              // Clear custom_name when switching to CUSTOM mode
              custom_name: type === "CUSTOM" ? "" : mapping.custom_name,
            }
          : mapping
      )
    );
  };

  const handleGenerateDescription = async (index: number) => {
    const mapping = columnMappings[index];
    if (!mapping) return;

    // Set loading state
    setColumnMappings((prev) =>
      prev.map((m, i) =>
        i === index ? { ...m, isGeneratingDescription: true } : m
      )
    );

    try {
      // Use custom_name if set, otherwise use column_name
      const bulletPointName =
        mapping.mapped_to_type && mapping.mapped_to_type !== "CUSTOM"
          ? mapping.mapped_to_type
          : mapping.custom_name || mapping.column_name;

      const response = await generateBulletPointDescription({
        bulletPointName,
        sampleValues: mapping.sample_values,
      });

      // Limit to 500 characters (database column limit)
      const description = (response.data?.data?.description || "").slice(0, 500);

      setColumnMappings((prev) =>
        prev.map((m, i) =>
          i === index
            ? { ...m, description, isGeneratingDescription: false }
            : m
        )
      );
    } catch (err) {
      setColumnMappings((prev) =>
        prev.map((m, i) =>
          i === index ? { ...m, isGeneratingDescription: false } : m
        )
      );
      setError("Failed to generate description. Please try again.");
    }
  };

  const handleSave = async (skipMappings: boolean = false) => {
    setSaving(true);
    setError(null);

    try {
      // Build custom bullet points mapping
      const customBulletPoints = skipMappings
        ? []
        : columnMappings
            .filter(
              (m) => m.selected && (m.mapped_to_type || m.custom_name.trim())
            )
            .map((m) => ({
              column_name: m.column_name,
              custom_name: m.custom_name.trim(),
              description: m.description.trim() || undefined,
              mapped_to_type:
                m.mapped_to_type === "CUSTOM" ? undefined : m.mapped_to_type,
            }));

      // Confirm configuration with custom bullet points
      await confirmBatchConfiguration({
        batchId,
        customBulletPoints,
      });

      // Start preprocessing
      await startPreprocess({ batchId });

      onComplete();
    } catch (err) {
      setError("Failed to save configuration. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSheetUploadSuccess = async () => {
    if (onSheetUploaded) {
      await onSheetUploaded();
    }
    // Reset mappings so they get re-initialized with new data
    setMappingsInitialized(false);
    setColumnMappings([]);
  };

  const selectedCount = columnMappings.filter((m) => m.selected).length;

  // If reconfiguring but no sheet data, treat as fresh start (show upload step)
  const shouldShowUploadStep = !isReconfiguring || !sheetData;

  // Validation: check if all selected columns have valid configuration
  // Returns count of columns with errors
  const validationErrorCount = columnMappings.filter((m) => {
    if (!m.selected) return false; // Not selected, no validation needed

    // Check if bullet point type is selected
    const hasValidBulletPoint =
      (m.mapped_to_type && m.mapped_to_type !== "CUSTOM") ||
      (m.mapped_to_type === "CUSTOM" && m.custom_name.trim().length > 0);

    if (!hasValidBulletPoint) return true; // Missing bullet point type

    // Check if description is provided
    if (!m.description.trim()) return true; // Missing description

    return false;
  }).length;

  const hasValidationErrors = validationErrorCount > 0;

  // Show loading state when auto-completing for non-AI users
  if (autoCompleting) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Setting up batch configuration...
        </Typography>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Box>
    );
  }

  // For GDrive batches, skip the upload step
  if (hasGdriveFolder) {
    return (
      <Box sx={{ width: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Batch Configuration
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<SkipNext />}
            onClick={() => handleSave(true)}
            disabled={saving}
          >
            Skip & Start Processing
          </Button>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Custom Bullet Points Section */}
        <CustomBulletPointsSection
          columnMappings={columnMappings}
          onToggleColumn={handleToggleColumn}
          onCustomNameChange={handleCustomNameChange}
          onDescriptionChange={handleMappingDescriptionChange}
          onMappedTypeChange={handleMappedTypeChange}
          onGenerateDescription={handleGenerateDescription}
          selectedCount={selectedCount}
          validationErrorCount={validationErrorCount}
          expanded={customBpExpanded}
          onExpandedChange={setCustomBpExpanded}
          loading={!sheetData}
        />

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          {validationErrorCount > 0 && (
            <Typography variant="body2" color="error">
              {validationErrorCount} column{validationErrorCount > 1 ? "s" : ""}{" "}
              with validation errors
            </Typography>
          )}
          <Button
            onClick={() => handleSave(false)}
            variant="contained"
            disabled={saving || hasValidationErrors || selectedCount === 0}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? "Processing..." : "Save & Start Processing"}
          </Button>
        </Box>
      </Box>
    );
  }

  // For client-owned batches, show stepper with upload + settings
  return (
    <Paper elevation={2} sx={{ p: 4, width: "100%" }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        {isReconfiguring ? "Re-setup Batch" : "Batch Setup"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isReconfiguring
          ? "Review and update your column mappings. Jobs in NOT_READY status will be reprocessed."
          : canSeeBatchConfiguration
            ? "Upload your master file and configure custom settings."
            : "Upload your master file to get started."}
      </Typography>

      <Stepper
        activeStep={shouldShowUploadStep ? activeStep : 0}
        orientation="vertical"
        nonLinear
      >
        {/* Step 1: Upload Master File - Hidden when reconfiguring with existing data */}
        {shouldShowUploadStep && (
          <Step completed={!!sheetData && activeStep !== 0}>
            <StepLabel
              StepIconProps={{
                icon:
                  sheetData && activeStep !== 0 ? (
                    <CheckCircle color="success" />
                  ) : (
                    <CloudUpload />
                  ),
              }}
              onClick={() => goToStep(0)}
              sx={{ cursor: "pointer" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Upload Master File
                </Typography>
                {sheetData && activeStep !== 0 && (
                  <Chip
                    label={sheetData.file_name || "Uploaded"}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
                {sheetData && activeStep !== 0 && (
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={(e) => {
                      e.stopPropagation();
                      goToStep(0);
                    }}
                  >
                    Change
                  </Button>
                )}
              </Box>
            </StepLabel>
            <StepContent
              TransitionProps={{ in: activeStep === 0 }}
              sx={{ py: activeStep === 0 ? 2 : 0 }}
            >
              <MasterFileUploadSection
                batchId={batchId}
                hasExistingFile={!!sheetData}
                fileName={sheetData?.file_name}
                sheetData={sheetData}
                isProcessing={isFetchingSheetData}
                processingMessage={
                  isFetchingSheetData ? "Loading sheet data..." : undefined
                }
                onUploadSuccess={handleSheetUploadSuccess}
                onContinue={canSeeBatchConfiguration ? () => goToStep(1) : undefined}
              />
            </StepContent>
          </Step>
        )}

        {/* Step 2: Configure Settings - Only shown when AI is available */}
        {canSeeBatchConfiguration && (
          <Step>
            <StepLabel
              StepIconProps={{
                icon: <Settings />,
              }}
              onClick={() => sheetData && goToStep(1)}
              sx={{ cursor: sheetData ? "pointer" : "default" }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="medium"
                color={sheetData ? "text.primary" : "text.disabled"}
              >
                {isReconfiguring ? "Review & Update Mappings" : "Configure Settings"}
              </Typography>
            </StepLabel>
            <StepContent
              TransitionProps={{ in: !shouldShowUploadStep || activeStep === 1 }}
            >
              <Box sx={{ pt: 1 }}>
                {error && (
                  <Alert
                    severity="error"
                    onClose={() => setError(null)}
                    sx={{ mb: 2 }}
                  >
                    {error}
                  </Alert>
                )}

                {/* Refreshing indicator - shown when re-uploading master file */}
                {isFetchingSheetData && (
                  <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    icon={<CircularProgress size={20} />}
                  >
                    Loading master file data...
                  </Alert>
                )}

                {/* Custom Bullet Points Section */}
                <CustomBulletPointsSection
                  columnMappings={columnMappings}
                  onToggleColumn={handleToggleColumn}
                  onCustomNameChange={handleCustomNameChange}
                  onDescriptionChange={handleMappingDescriptionChange}
                  onMappedTypeChange={handleMappedTypeChange}
                  onGenerateDescription={handleGenerateDescription}
                  selectedCount={selectedCount}
                  validationErrorCount={validationErrorCount}
                  expanded={customBpExpanded}
                  onExpandedChange={setCustomBpExpanded}
                  loading={!sheetData || isFetchingSheetData}
                />

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  {validationErrorCount > 0 && (
                    <Typography variant="body2" color="error">
                      {validationErrorCount} column
                      {validationErrorCount > 1 ? "s" : ""} with validation errors
                    </Typography>
                  )}
                  {isReconfiguring && onCancel && (
                    <Button
                      onClick={onCancel}
                      variant="outlined"
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    onClick={() => handleSave(false)}
                    variant="contained"
                    color={isReconfiguring ? "warning" : "primary"}
                    disabled={
                      saving || hasValidationErrors || selectedCount === 0
                    }
                    startIcon={
                      saving ? (
                        <CircularProgress size={16} />
                      ) : isReconfiguring ? (
                        <Refresh />
                      ) : null
                    }
                  >
                    {saving
                      ? "Processing..."
                      : isReconfiguring
                        ? "Save & Reprocess NOT_READY"
                        : "Save & Start Processing"}
                  </Button>
                </Box>
              </Box>
            </StepContent>
          </Step>
        )}
      </Stepper>
    </Paper>
  );
};
